<template>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
    <!-- Derniers matchs -->
    <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 transform transition-all duration-500 hover:shadow-amber-900/40 hover:scale-[1.02] animate-slideUp box-glow">
      <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
        <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
        <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          Derniers matchs
        </h2>
      </div>
      <div class="relative">
        <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
        <div v-if="recentMatches.length === 0" class="p-6 text-center relative">
          <p class="text-amber-200/60">Aucun match récent à afficher</p>
        </div>
        <ul v-else role="list" class="divide-y divide-amber-900/30 relative">
          <li v-for="(match, index) in recentMatches" :key="index" 
              class="px-5 py-4 transition-colors duration-200 hover:bg-amber-900/20 relative"
              :class="{ 'bg-black/80': index % 2 === 0, 'bg-amber-950/40': index % 2 !== 0 }">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center">
                    <span class="font-medium text-amber-200 truncate">{{ match.player1 }}</span>
                    <span class="mx-2 text-amber-500">vs</span>
                    <span class="font-medium text-amber-200 truncate">{{ match.player2 }}</span>
                  </div>
                  <div class="mt-1 flex items-center">
                    <span class="px-2 py-1 text-xs font-medium bg-amber-900/30 text-amber-300 rounded border border-amber-700/30 shadow-inner">
                      {{ match.score1 }} - {{ match.score2 }}
                    </span>
                    <span class="ml-2 text-xs text-amber-200/60">{{ match.date }}</span>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
        <div class="bg-gradient-to-r from-amber-900/40 via-black/90 to-amber-900/40 px-4 py-3 flex justify-center relative border-t border-amber-800/40">
          <router-link to="/matches" class="text-amber-400 hover:text-amber-300 inline-flex items-center space-x-1 transition-all duration-200 hover:translate-x-1">
            <span>Voir tous les matchs</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Top 5 du classement -->
    <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 transform transition-all duration-500 hover:shadow-amber-900/40 hover:scale-[1.02] animate-slideUp box-glow">
      <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
        <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
        <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Top 5 du classement
        </h2>
      </div>
      <div class="relative">
        <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
        <div v-if="topPlayers.length === 0" class="p-6 text-center relative">
          <p class="text-amber-200/60">Aucun joueur à afficher</p>
        </div>
        <ul v-else role="list" class="divide-y divide-amber-900/30 relative">
          <li v-for="(player, index) in topPlayers" :key="index" 
              class="px-5 py-4 transition-colors duration-200 hover:bg-amber-900/20 relative"
              :class="{ 'bg-black/80': index % 2 === 0, 'bg-amber-950/40': index % 2 !== 0 }">
            <div class="flex items-center">
              <div class="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-lg shadow-md border border-amber-600/30" :class="getRankClass(index)">
                <span class="text-black font-bold">{{ index + 1 }}</span>
              </div>
              <div class="min-w-0 flex-1 px-4">
                <div class="flex justify-between items-center">
                  <p class="text-md font-medium text-amber-200 truncate">{{ player.name }}</p>
                  <p class="text-amber-300 font-semibold px-2 py-1 bg-amber-900/30 rounded-lg text-sm border border-amber-700/30 shadow-inner">
                    {{ player.points }} pts
                  </p>
                </div>
              </div>
            </div>
          </li>
        </ul>
        <div class="bg-gradient-to-r from-amber-900/40 via-black/90 to-amber-900/40 px-4 py-3 flex justify-center relative border-t border-amber-800/40">
          <router-link to="/rankings" class="text-amber-400 hover:text-amber-300 inline-flex items-center space-x-1 transition-all duration-200 hover:translate-x-1">
            <span>Voir le classement complet</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardModules',
  props: {
    recentMatches: {
      type: Array,
      default: () => []
    },
    topPlayers: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    getRankClass(index) {
      const classes = [
        'bg-gradient-to-r from-amber-300 to-amber-400', // Or (1er)
        'bg-gradient-to-r from-gray-300 to-gray-400',   // Argent (2e)
        'bg-gradient-to-r from-amber-700 to-amber-800', // Bronze (3e)
        'bg-gradient-to-r from-amber-900 to-gray-800',  // 4e
        'bg-gradient-to-r from-gray-800 to-amber-900'   // 5e
      ];
      return classes[index] || 'bg-gradient-to-r from-amber-900 to-gray-800';
    }
  }
}
</script>

<style scoped>
@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes slideUp {
  0% { 
    opacity: 0;
    transform: translateY(30px);
  }
  100% { 
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-out forwards;
}

.animate-slideUp {
  animation: slideUp 0.8s ease-out forwards;
}

.pattern-warhammer {
  background-image: 
    url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 0L0 12l12 12 12-12z' fill='%23D4AF37' fill-opacity='0.2'/%3E%3C/svg%3E"),
    url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0l20 20-20 20L0 20z' fill='%23D4AF37' fill-opacity='0.1'/%3E%3C/svg%3E");
  background-size: 30px 30px, 60px 60px;
}

.box-glow {
  box-shadow: 0 0 30px rgba(217, 119, 6, 0.15), 
              0 5px 20px rgba(0, 0, 0, 0.3), 
              inset 0 0 15px rgba(217, 119, 6, 0.05);
}
</style> 